import { InputHTMLAttributes, useEffect, useRef } from 'react'

import Icon from '../../ui/Icon'

type SearchInputProps = InputHTMLAttributes<HTMLInputElement> & {
  iconName?: string
  iconSize?: number
  shortcutKey?: string
}

export const SearchInput = ({
  iconName,
  iconSize = 15,
  shortcutKey,
  className = '',
  ...inputProps
}: SearchInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)

  useEffect(() => {
    if (!shortcutKey) {
      return
    }

    const handleShortcut = (event: KeyboardEvent) => {
      const target = event.target
      const input = inputRef.current

      if (!input || input.offsetParent === null) {
        return
      }

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return
      }

      const hasCorrectModifier = isMac ? event.metaKey : event.ctrlKey

      const hasCorrectKey =
        event.key.toLowerCase() === shortcutKey.toLowerCase()

      if (!hasCorrectModifier || !hasCorrectKey) {
        return
      }

      event.preventDefault()
      input.focus()
    }

    window.addEventListener('keydown', handleShortcut)

    return () => {
      window.removeEventListener('keydown', handleShortcut)
    }
  }, [isMac, shortcutKey])

  const searchClassName = ['ph-search', className].filter(Boolean).join(' ')

  const shortcutLabel = shortcutKey
    ? `${isMac ? '⌘' : 'Ctrl+'}${shortcutKey.toUpperCase()}`
    : null

  const ariaShortcut = shortcutKey
    ? `${isMac ? 'Meta' : 'Control'}+${shortcutKey.toUpperCase()}`
    : undefined

  return (
    <div className={searchClassName}>
      {iconName && <Icon name={iconName} size={iconSize} />}

      <input
        ref={inputRef}
        type="search"
        aria-keyshortcuts={ariaShortcut}
        {...inputProps}
      />

      {shortcutLabel && (
        <span className="mono tiny" aria-hidden="true">
          {shortcutLabel}
        </span>
      )}
    </div>
  )
}
